import { PaginatedResponse, useApi } from '@/api';
import { Asset, HyperLiquidLink, Link } from '@/components';
import { DEFAULT_NETWORK } from '@/constants';
import {
  ArrowRightIcon,
  Box,
  BoxProps,
  CurrencyValue,
  DataTable,
  DropdownFilter,
  Flex,
  Heading,
  PercentageValue,
  RelativeDateTime,
  TableSkeleton,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import _ from 'lodash';
import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

const Label = styled(Box)<BoxProps>`
  color: ${(props) => props.theme.colors.muted};
  font-size: ${(props) => props.theme.sizes['-1']};
`;

type Args = Record<string, string | number | boolean>;

type ParamsData = {
  datetime: string;
  order_index: string;
  event: string;
  args: Args;
  underlying_address: string;
  underlying_name: string;
  underlying_symbol: string;
  tx_hash: string;
};

const EVENT_TYPE_OPTIONS = [
  { label: 'All events', value: '' },
  {
    label: 'AToken Upgraded',
    value: 'ATokenUpgraded'
  },
  {
    label: 'Borrow Cap Changed',
    value: 'BorrowCapChanged'
  },
  {
    label: 'Borrowable In Isolation Changed',
    value: 'BorrowableInIsolationChanged'
  },
  { label: 'Bridge Protocol Fee Updated', value: 'BridgeProtocolFeeUpdated' },
  {
    label: 'Collateral Configuration Changed',
    value: 'CollateralConfigurationChanged'
  },
  { label: 'Debt Ceiling Changed', value: 'DebtCeilingChanged' },
  { label: 'EMode Asset Category Changed', value: 'EModeAssetCategoryChanged' },
  { label: 'EMode Category Added', value: 'EModeCategoryAdded' },
  {
    label: 'Flashloan Premium To Protocol Updated',
    value: 'FlashloanPremiumToProtocolUpdated'
  },
  {
    label: 'Flashloan Premium Total Updated',
    value: 'FlashloanPremiumTotalUpdated'
  },
  {
    label: 'Liquidation Protocol Fee Changed',
    value: 'LiquidationProtocolFeeChanged'
  },
  {
    label: 'Reserve Active',
    value: 'ReserveActive'
  },
  { label: 'Reserve Borrowing', value: 'ReserveBorrowing' },
  { label: 'Reserve Dropped', value: 'ReserveDropped' },
  { label: 'Reserve Factor Changed', value: 'ReserveFactorChanged' },
  { label: 'Reserve Flash Loaning', value: 'ReserveFlashLoaning' },
  { label: 'Reserve Frozen', value: 'ReserveFrozen' },
  { label: 'Reserve Initialized', value: 'ReserveInitialized' },
  {
    label: 'Reserve Interest Rate Strategy Changed',
    value: 'ReserveInterestRateStrategyChanged'
  },
  { label: 'Reserve Paused', value: 'ReservePaused' },

  {
    label: 'Reserve Stable Rate Borrowing',
    value: 'ReserveStableRateBorrowing'
  },

  { label: 'Siloed Borrowing Changed', value: 'SiloedBorrowingChanged' },
  { label: 'Supply Cap Changed', value: 'SupplyCapChanged' },
  { label: 'Unbacked Mint Cap Changed', value: 'UnbackedMintCapChanged' }
];

/* splits event name on capital letters */
function splitCaps(text: string) {
  if (typeof text !== 'string') {
    return text;
  }
  return text.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function ValueComponent({
  data,
  field,
  percentage
}: {
  data: Args;
  field: string;
  percentage: boolean;
}) {
  const value = data[field];
  if (_.isBoolean(value)) {
    return String(value);
  }
  if (percentage) {
    return <PercentageValue value={Number(value) / 100 / 100} />;
  }
  return <CurrencyValue value={value} />;
}

function ArrowComponent({
  data,
  newKey,
  oldKey
}: {
  data: Args;
  newKey: string;
  oldKey: string;
}) {
  const newValue = data[newKey];
  const oldValue = data[oldKey];

  let color = 'white';
  if (_.isBoolean(newValue) || _.isBoolean(oldValue)) {
    if (newValue === true) {
      color = 'success';
    } else {
      color = 'error';
    }
  } else {
    if (Number(newValue) > Number(oldValue)) {
      color = 'success';
    } else {
      color = 'error';
    }
  }

  return (
    <Flex color={color}>
      <ArrowRightIcon size="1" />
    </Flex>
  );
}

function formatGenericEventsOldNew(
  row: ParamsData,
  newKey: string,
  oldKey: string,
  percentage = false
): ReactNode {
  return (
    <Flex alignItems="baseline" gap="2xs">
      <Label>{splitCaps(newKey.replace('new', ''))}:</Label>
      <Flex alignItems="center" gap="3xs">
        <ValueComponent
          data={row.args}
          field={oldKey}
          percentage={percentage}
        />
        <ArrowComponent data={row.args} newKey={newKey} oldKey={oldKey} />
        <ValueComponent
          data={row.args}
          field={newKey}
          percentage={percentage}
        />
      </Flex>
    </Flex>
  );
}

function formatEventReserveInitialzed(
  row: ParamsData,
  network: string
): ReactNode {
  return (
    <Flex flexDirection="column">
      <Flex alignItems="baseline" gap="2xs">
        <Label>aToken Address:</Label>
        <WalletAddress
          address={String(row.args['aToken'])}
          options={{
            inline: true,
            showBlockie: false,
            showEthScanLink: false,
            network
          }}
        />
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Stable Debt Token Address:</Label>
        <WalletAddress
          address={String(row.args['stableDebtToken'])}
          options={{
            inline: true,
            showBlockie: false,
            showEthScanLink: false,
            network
          }}
        />
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Variable Debt Token Address:</Label>
        <WalletAddress
          address={String(row.args['variableDebtToken'])}
          options={{
            inline: true,
            showBlockie: false,
            showEthScanLink: false,
            network
          }}
        />
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Interest Rate Strategy Address:</Label>
        <WalletAddress
          address={String(row.args['interestRateStrategyAddress'])}
          options={{
            inline: true,
            showBlockie: false,
            showEthScanLink: false,
            network
          }}
        />
      </Flex>
    </Flex>
  );
}
function formatEventCollateralConfigurationChanged(row: ParamsData): ReactNode {
  return (
    <Flex flexDirection="column">
      <Flex alignItems="baseline" gap="2xs">
        <Label>LTV:</Label>
        <PercentageValue value={Number(row.args['ltv']) / 100 / 100} />
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Liquidation Bonus:</Label>
        <PercentageValue
          value={(Number(row.args['liquidationBonus']) / 100 - 100) / 100}
        />
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Liquidation Threshold:</Label>
        <PercentageValue
          value={Number(row.args['liquidationThreshold']) / 100 / 100}
        />
      </Flex>
    </Flex>
  );
}

function formatEventEModeCategoryAdded(row: ParamsData): ReactNode {
  return (
    <Flex flexDirection="column">
      <Flex alignItems="baseline" gap="2xs">
        <Label>LTV:</Label>
        <PercentageValue value={Number(row.args['ltv']) / 100 / 100} />
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Label:</Label> {row.args['label']}
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Liquidation Bonus:</Label>
        <PercentageValue
          value={(Number(row.args['liquidationBonus']) / 100 - 100) / 100}
        />
      </Flex>
      <Flex alignItems="baseline" gap="2xs">
        <Label>Liquidation Threshold:</Label>
        <PercentageValue
          value={Number(row.args['liquidationThreshold']) / 100 / 100}
        />
      </Flex>
    </Flex>
  );
}

function formatEventStrategyOldNew(
  row: ParamsData,
  network: string
): ReactNode {
  return (
    <Flex alignItems="baseline" gap="2xs">
      <Label>New Strategy Address:</Label>
      <WalletAddress
        address={String(row.args['newStrategy'])}
        options={{
          inline: true,
          showBlockie: false,
          showEthScanLink: false,
          network
        }}
      />
    </Flex>
  );
}

function formatFlashloanPremiumTotalUpdated(
  row: ParamsData,
  newKey: string,
  oldKey: string
): ReactNode {
  return (
    <Flex alignItems="baseline" gap="2xs">
      <Label>{splitCaps(newKey.replace('new', ''))}:</Label>
      <Flex gap="3xs">
        <PercentageValue value={Number(row.args[oldKey]) / 100} />
        <ArrowComponent data={row.args} newKey={newKey} oldKey={oldKey} />
        <PercentageValue value={Number(row.args[newKey]) / 100} />
      </Flex>
    </Flex>
  );
}

function formatGenericBooleanEvents(row: ParamsData) {
  const entries = Object.entries(row.args);
  return (
    <Flex alignItems="baseline" gap="2xs">
      <Label>{_.capitalize(entries[1][0])}:</Label>
      {String(entries[1][1])}
    </Flex>
  );
}

export default function ParamsChangesTable() {
  const network = DEFAULT_NETWORK;

  const { sorting, pagination, filters } = useTableProps({
    pageSize: 5,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: { eventType: '' }
  });
  const [data, error] = useApi<PaginatedResponse<ParamsData[]>>({
    url: '/params/events/',
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      event_type: filters.filtersApplied.eventType
    }
  });

  const eventFormattersMap: Record<string, (row: ParamsData) => ReactNode> =
    useMemo(
      () => ({
        /* formats Reserve Initialized Event only */
        ReserveInitialized: (row) => formatEventReserveInitialzed(row, network),

        /* formats Collateral Configuration Changed Event only */
        CollateralConfigurationChanged: (row) =>
          formatEventCollateralConfigurationChanged(row),

        /* formats EMode Category Added Event only */
        EModeCategoryAdded: (row) => formatEventEModeCategoryAdded(row),

        /* formats Reserve Interest Rate Strategy Changed Event only */
        ReserveInterestRateStrategyChanged: (row) =>
          formatEventStrategyOldNew(row, network),

        /* formats Flashloan PremiumTotal Updated Event only */
        FlashloanPremiumTotalUpdated: (row) =>
          formatFlashloanPremiumTotalUpdated(
            row,
            'newFlashloanPremiumTotal',
            'oldFlashloanPremiumTotal'
          ),

        /* formats One line boolean Events generic */
        ReserveFrozen: (row) => formatGenericBooleanEvents(row),
        BorrowableInIsolationChanged: (row) => formatGenericBooleanEvents(row),
        ReserveStableRateBorrowing: (row) => formatGenericBooleanEvents(row),
        ReserveActive: (row) => formatGenericBooleanEvents(row),
        ReserveBorrowing: (row) => formatGenericBooleanEvents(row),
        ReserveFlashLoaning: (row) => formatGenericBooleanEvents(row),
        ReserveDropped: (row) => formatGenericBooleanEvents(row),
        ReservePaused: (row) => formatGenericBooleanEvents(row),

        /* formats Old / New changes Events generic */
        BorrowCapChanged: (row) =>
          formatGenericEventsOldNew(row, 'newBorrowCap', 'oldBorrowCap'),
        SupplyCapChanged: (row) =>
          formatGenericEventsOldNew(row, 'newSupplyCap', 'oldSupplyCap'),
        ReserveFactorChanged: (row) =>
          formatGenericEventsOldNew(
            row,
            'newReserveFactor',
            'oldReserveFactor',
            true
          ),

        DebtCeilingChanged: (row) =>
          formatGenericEventsOldNew(row, 'newDebtCeiling', 'oldDebtCeiling'),
        EModeAssetCategoryChanged: (row) =>
          formatGenericEventsOldNew(row, 'newCategoryId', 'oldCategoryId'),
        UnbackedMintCapChanged: (row) =>
          formatGenericEventsOldNew(
            row,
            'newUnbackedMintCap',
            'oldUnbackedMintCap'
          ),
        SiloedBorrowingChanged: (row) =>
          formatGenericEventsOldNew(row, 'newState', 'oldState'),
        LiquidationProtocolFeeChanged: (row) =>
          formatGenericEventsOldNew(row, 'newFee', 'oldFee', true),
        BridgeProtocolFeeUpdated: (row) =>
          formatGenericEventsOldNew(
            row,
            'newBridgeProtocolFee',
            'oldBridgeProtocolFee'
          )
      }),
      [network]
    );

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn<ParamsData>[] = [
    {
      header: '',
      getCell: (row) => (
        <Link to={`/markets/${row.underlying_address}`}>
          {row.underlying_address ? (
            <Asset
              symbol={row.underlying_symbol}
              address={row.underlying_address}
              network={network}
              name={row.underlying_name}
              iconSize="3"
            />
          ) : (
            <Flex flexDirection="column" gap="3xs">
              <Box color="muted" fontSize="-2">
                Hyperlend Protocol
              </Box>
            </Flex>
          )}
        </Link>
      )
    },
    {
      header: 'Event',
      getCell: (row) => splitCaps(row.event)
    },
    {
      header: 'Parameter Changes',
      getCell: (row) => (
        <>
          {eventFormattersMap[row.event]
            ? eventFormattersMap[row.event](row)
            : null}
        </>
      ),
      columnSize: ['3fr']
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <HyperLiquidLink path={`tx/${row.tx_hash}`} />
        </Flex>
      ),
      sortingField: 'datetime',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Flex flexDirection="column" gap={['m', 's']}>
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="3">Parameter Changes</Heading>
        <DropdownFilter
          withCryptoIcon={false}
          filterField="eventType"
          options={EVENT_TYPE_OPTIONS}
          pagination={pagination}
          {...filters}
        />
      </Flex>

      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
      />
    </Flex>
  );
}
